import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import { useAccount } from '../../context/AccountContext';
import { GET_TINYCMS_ANALYTICS, GET_TINYCMS_INTEGRATIONS } from '../../graphql';
import type {
  AnalyticsTopPage,
  SourceKey,
  TodayPerformance,
  WeekCount,
  WebsiteAnalytics,
} from './todayData';
import {
  chartBars,
  countOf,
  Days,
  Ghost,
  GhostChart,
  GhostTile,
  Legend,
  RangeSwitch,
  TipBars,
} from './analyticsParts';
import { AnalyticsSource, ConnectCard } from './AnalyticsConnect';
import { barsOf, pageName } from './analyticsMath';
import { useAddons } from './addons';
import { HomeTag, isHomePath } from './HomeTag';
import { RollNumber } from '../common/RollNumber';
import { SearchSection } from './SearchAnalytics';
import { SectionLocked } from './SectionShell';

const CODE_AND_TRACKING = '/content?view=manage&area=code#analytics';

/**
 * Content → Analytics ("TinyCMS UX optimization review", App Prototype →
 * Website analytics): a range, four numbers, visitors per day, top articles
 * and where visitors come from. Form responses are TinyCMS's own count. The
 * visitor numbers come from Google Analytics, as the engine last synced them
 * (never a live call, so the page is as quick as the rest of the app). Until
 * GA is connected they're drawn in grey under a Connect card, never made up.
 * Business and Pro only: Free and Personal get a short upgrade card here, and
 * keep this week's visits and top pages on Today (owner 2026-10-31).
 */

/** "+8% vs previous 7 days", "None in the previous 7 days". */
function change(c: WeekCount | undefined, days: number) {
  const vs = `vs previous ${days} days`;
  if (!c) return { lead: '', rest: vs, tone: 'up' };
  if (!c.previous) return { lead: '', rest: `None in the previous ${days} days`, tone: 'up' };
  const pct = Math.round(((c.count - c.previous) / c.previous) * 100);
  if (pct === 0) return { lead: 'Same', rest: ` as the previous ${days} days`, tone: 'up' };
  return {
    lead: `${pct > 0 ? '+' : '−'}${Math.abs(pct)}%`,
    rest: ` ${vs}`,
    tone: pct > 0 ? 'up' : 'down',
  };
}

const SOURCE_WORDS: Record<SourceKey, string> = {
  search: 'Search',
  direct: 'Direct',
  social: 'Social',
  email: 'Email',
  referral: 'Other sites',
  other: 'Other',
};

const COLORS = {
  search: 'var(--ink-accent)',
  social: 'var(--ink-ink)',
  other: 'var(--ink-line3)',
};
const SERIES = { search: 'From search', social: 'From social', other: 'Everything else' };

const Tile: React.FC<{ label: string; hint: string; value: WeekCount; days: number }> = ({
  label,
  hint,
  value,
  days,
}) => {
  const delta = change(value, days);
  return (
    <div className="an-tile" title={hint}>
      <span className="an-tile-label">{label}</span>
      <span className="an-tile-value">
        <RollNumber value={value.count} />
      </span>
      <span className="an-tile-sub">
        {delta.lead ? <span data-tone={delta.tone}>{delta.lead}</span> : null}
        {delta.rest}
      </span>
    </div>
  );
};

const VisitorChart: React.FC<{ website: WebsiteAnalytics; days: Days }> = ({ website, days }) => {
  const bars = barsOf(website.daily, days);
  const max = Math.max(1, ...bars.map((b) => b.search + b.social + b.other));
  const x = chartBars(days);
  return (
    <>
      <TipBars
        label={`Visitors per ${x.unit}`}
        gap={days === 7 ? 16 : days === 28 ? 4 : 8}
        bars={bars.map((b) => ({
          key: b.day,
          title: b.label,
          total: countOf(b.visitors, 'visitor'),
          rows: (['search', 'social', 'other'] as const).map((k) => ({
            label: SERIES[k],
            value: b[k].toLocaleString(),
            color: COLORS[k],
          })),
          segments: (['other', 'social', 'search'] as const)
            .filter((k) => b[k])
            .map((k) => ({
              key: k,
              height: Math.max(2, Math.round((b[k] / max) * 168)),
              color: COLORS[k],
            })),
        }))}
      />
      <div className="an-x">
        <span>{bars.length ? bars[0].label : ''}</span>
        <span>{bars.length > 2 ? bars[Math.floor(bars.length / 2)].label : ''}</span>
        <span>{days === 90 && bars.length ? bars[bars.length - 1].label : 'Today'}</span>
      </div>
    </>
  );
};

const pageHref = (p: AnalyticsTopPage) =>
  p.kind === 'article' && p.id
    ? `/content/${p.id}`
    : p.kind === 'page' && p.id
      ? `/content?view=pages&page=${p.id}`
      : null;

/**
 * Ranked by the number the row shows, visitors (backlog a5ce20dc: the API's
 * order is page views, so Pricing's 3 sat below a 1), and each row says what
 * it is, as the design does ("Article · Top source…").
 */
const TopArticles: React.FC<{ pages: AnalyticsTopPage[] }> = ({ pages }) =>
  pages.length ? (
    <div className="an-rows">
      {[...pages]
        .sort((a, b) => b.visitors - a.visitors || b.views - a.views)
        .slice(0, 5)
        .map((p, i) => {
          const href = pageHref(p);
          const title = pageName(p);
          const kind = p.kind === 'article' ? 'Article' : p.kind === 'page' ? 'Page' : '';
          const sub = [
            kind,
            p.topSource
              ? `Top source: ${SOURCE_WORDS[p.topSource]} ${Math.round((p.topShare || 0) * 100)}%`
              : p.title
                ? p.path
                : '',
          ]
            .filter(Boolean)
            .join(' · ');
          return (
            <div key={p.path} className="an-row">
              <span className="an-rank">{i + 1}</span>
              <span className="an-row-main">
                {href ? (
                  <Link to={href} className="an-row-title">
                    {title}
                    {p.title && isHomePath(p.path) ? <HomeTag title={p.title} /> : null}
                  </Link>
                ) : (
                  <span className="an-row-title">
                    {title}
                    {p.title && isHomePath(p.path) ? <HomeTag title={p.title} /> : null}
                  </span>
                )}
                {sub ? <span className="an-row-sub">{sub}</span> : null}
              </span>
              <span className="an-row-n">{p.visitors.toLocaleString()}</span>
            </div>
          );
        })}
    </div>
  ) : (
    <p className="an-empty-line">No pages were visited in this range.</p>
  );

const Sources: React.FC<{ website: WebsiteAnalytics }> = ({ website }) =>
  website.sources.length ? (
    <div className="an-sources">
      {website.sources.map((s) => (
        <div key={s.key} className="an-source-row">
          <span>{s.label}</span>
          <span className="an-source-track">
            <span
              className="an-source-fill"
              style={{
                width: `${Math.max(1, Math.round(s.share * 100))}%`,
                background:
                  s.key === 'search'
                    ? COLORS.search
                    : s.key === 'social'
                      ? COLORS.social
                      : COLORS.other,
              }}
            />
          </span>
          <span className="an-source-pct">{Math.round(s.share * 100)}%</span>
        </div>
      ))}
    </div>
  ) : (
    <p className="an-empty-line">No visitors in this range.</p>
  );

const AnalyticsUpgrade: React.FC = () => {
  const { isOwner, ownerEmail } = useAddons();
  return (
    <div className="an-page" data-screen-label="Website analytics">
      <SectionLocked
        title="See where your visitors come from and how your site does over time, on Business"
        lede="Today keeps showing this week’s visits and top pages."
        points={[]}
      >
        {isOwner ? (
          <Link to="/settings/billing" className="today-sec-link">
            Compare plans <span aria-hidden>→</span>
          </Link>
        ) : (
          <p className="ink-muted">
            Only the site owner{ownerEmail ? `, ${ownerEmail},` : ''} can change the plan.
          </p>
        )}
      </SectionLocked>
    </div>
  );
};

export const CmsAnalytics: React.FC = () => {
  const { fullAnalytics } = useAddons();
  return fullAnalytics ? <FullAnalytics /> : <AnalyticsUpgrade />;
};

const FullAnalytics: React.FC = () => {
  const { accountId } = useAccount();
  const [days, setDays] = useState<Days>(7);
  const { data, loading, startPolling, stopPolling } = useQuery<{
    tinycmsAnalytics: TodayPerformance;
  }>(GET_TINYCMS_ANALYTICS, {
    variables: { accountId, days },
    skip: !accountId,
    fetchPolicy: 'cache-and-network',
  });
  const { data: intData } = useQuery(GET_TINYCMS_INTEGRATIONS, {
    variables: { accountId },
    skip: !accountId,
    fetchPolicy: 'cache-first',
  });
  const { reachOn } = useAddons();
  const perf = data?.tinycmsAnalytics;
  const fresh = perf && perf.days === days ? perf : null;
  const responses = fresh?.formResponses;
  const delta = change(responses, days);
  const unit = chartBars(days).unit;
  const website = fresh?.website;
  const connection = website?.connection || null;
  const ranges = website?.ranges || null;
  const live = Boolean(ranges);
  const waiting = Boolean(connection && !ranges);
  // Grey parts carry a tag only while the first numbers are on their way;
  // before that the Connect card says what to do. Never "Coming soon".
  const ghostTag = waiting ? 'Connected' : null;
  const canManage = Boolean(intData?.tinycmsIntegrations?.canManage);
  const available = intData?.tinycmsIntegrations?.googleAnalytics?.available !== false;

  // While the first numbers are on their way, look again every 15 seconds.
  useEffect(() => {
    if (waiting) startPolling(15000);
    else stopPolling();
    return () => stopPolling();
  }, [waiting, startPolling, stopPolling]);

  const formTile = (
    <Link
      to="/content?view=forms"
      className="an-tile"
      title="Responses to the forms on your site, from tinycms"
    >
      <span className="an-tile-label">Form responses</span>
      <span className="an-tile-value">
        {responses && !loading ? (
          <RollNumber value={responses.count} />
        ) : (
          <Ghost width={56} height={26} />
        )}
      </span>
      <span className="an-tile-sub">
        {delta.lead ? <span data-tone={delta.tone}>{delta.lead}</span> : null}
        {delta.rest}
      </span>
    </Link>
  );

  return (
    <div className="an-page" data-screen-label="Website analytics">
      <div className="an-top">
        <RangeSwitch days={days} onChange={setDays} />
        {connection ? (
          <AnalyticsSource accountId={accountId} connection={connection} />
        ) : (
          <span className="an-source">{fresh ? 'Google Analytics isn’t connected' : ''}</span>
        )}
      </div>

      {connection?.status === 'reconnect' ? (
        <div className="an-alert" role="status">
          <span>{connection.error || 'Google Analytics stopped sharing your numbers.'}</span>
          <Link to={CODE_AND_TRACKING}>Fix in Code &amp; tracking →</Link>
        </div>
      ) : connection?.error ? (
        <div className="an-note">{connection.error}</div>
      ) : null}

      {fresh && !connection ? (
        <ConnectCard
          tagOnly={Boolean(website?.tagOnly)}
          ga4Id={website?.ga4Id || null}
          canManage={canManage}
          available={available}
          connectTo={CODE_AND_TRACKING}
        />
      ) : null}

      <div className="an-tiles">
        {ranges ? (
          <>
            <Tile
              label="Visitors"
              hint="People who opened your site"
              value={ranges.visitors}
              days={days}
            />
            <Tile
              label="From search"
              hint="Visitors who arrived from Google and other search engines"
              value={ranges.search}
              days={days}
            />
            <Tile
              label="From social"
              hint="Visitors who came from a social network"
              value={ranges.social}
              days={days}
            />
          </>
        ) : (
          <>
            <GhostTile label="Visitors" hint="People who opened your site" />
            <GhostTile
              label="From search"
              hint="Visitors who arrived from Google and other search engines"
            />
            <GhostTile label="From social" hint="Visitors who came from a social network" />
          </>
        )}
        {formTile}
      </div>
      {/* Why From search and Search Console's clicks differ (Round 32 C, backlog dc8b1822). */}
      {live ? (
        <p className="an-note an-search-note">
          From search counts visits Google Analytics can trace to a search engine. Search Console,
          below, counts clicks on Google results, so the two rarely match.
        </p>
      ) : null}

      <div className="an-card an-chart">
        <div className="an-card-head">
          <div>
            <div className="an-card-title">Visitors per {unit}</div>
            <div className="an-card-sub">
              Each bar is one {unit}.{live ? ' Point at or tap a bar for its numbers.' : ''}
            </div>
          </div>
          <Legend
            items={[
              ['From search', COLORS.search],
              ['From social', COLORS.social],
              ['Everything else', COLORS.other],
            ]}
          />
        </div>
        {live && website ? (
          <VisitorChart website={website} days={days} />
        ) : (
          <GhostChart days={days} tag={ghostTag} what="Bringing in your numbers" />
        )}
      </div>

      <div className="an-pair">
        <div className="an-card">
          <div className="an-card-head">
            {/* Pages and articles both (Prototype; backlog d88e5d0d). */}
            <div className="an-card-title">Top pages and articles</div>
            <span className="an-card-sub">Visitors</span>
          </div>
          {live && website ? (
            <TopArticles pages={website.topPages} />
          ) : (
            <div className="an-soon">
              <div className="an-rows" aria-hidden="true">
                {[74, 62, 80, 56, 68].map((w, i) => (
                  <div key={i} className="an-row">
                    <span className="an-rank">{i + 1}</span>
                    <span>
                      <span className="ink-ghost" style={{ width: `${w}%`, height: 11 }} />
                      <span
                        className="ink-ghost"
                        style={{ width: '34%', height: 9, marginTop: 7 }}
                      />
                    </span>
                    <span className="ink-ghost" style={{ width: 40, height: 11 }} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="an-card">
          <div className="an-card-head">
            <div className="an-card-title">Where visitors come from</div>
            <span className="an-card-sub">Share of visitors</span>
          </div>
          {live && website ? (
            <>
              <Sources website={website} />
              {reachOn ? (
                <p className="an-card-foot">
                  Social is every visitor Google Analytics says came from a social network, however
                  the link was shared. <Link to="/reach/analytics">Social → Analytics</Link> counts
                  only clicks on the links in your posts, and which posts sent them.
                </p>
              ) : null}
            </>
          ) : (
            <div className="an-soon">
              <div className="an-sources" aria-hidden="true">
                {(
                  [
                    ['Search', 44],
                    ['Direct', 21],
                    ['Social', 20],
                    ['Email', 9],
                    ['Other sites', 6],
                  ] as Array<[string, number]>
                ).map(([label, w]) => (
                  <div key={label} className="an-source-row">
                    <span>{label}</span>
                    <span className="an-source-track">
                      <span
                        className="ink-ghost"
                        style={{ width: `${w * 1.8}%`, height: '100%' }}
                      />
                    </span>
                    <span
                      className="ink-ghost"
                      style={{ width: 30, height: 10, justifySelf: 'end' }}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="an-note">
        {live
          ? 'Visitors are people, counted once per range by Google Analytics; a day’s bar counts that day’s visitors. Form responses come from your tinycms forms, so they match Forms exactly.'
          : 'Form responses come from your tinycms forms, so they match Forms exactly.'}
      </div>

      <SearchSection days={days} />
    </div>
  );
};
